import * as React from 'react';
import styled, { css, useTheme } from 'styled-components';
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {SystemIcons} from '../icons';
import {Size} from '../types';
import {ComponentLStyling, ComponentMStyling, ComponentSStyling} from '../styles';
import { DropdownItem } from '../Dropdown';
import {defaultOnMouseDownHandler} from '../common';

export interface MenuItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'id' | 'disabled' | 'onMouseDown' | 'onClick'> {
  /** Required. Details of the item. */
  item: DropdownItem;
  /** Required. Handler to be called when user clicks on the menu item */
  onClickHandler: (e: React.MouseEvent) => void;
  /** Required. Styles are different for MenuItems in 'active' state.  */
  active: boolean;
  /** Required. Id of the MenuItem.  */
  id: string;
  /** Optional. Size of the component. Defaults to 'medium'.  */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Optional. Icon to be shown on the right side of the menu item. Icon will be shown only if 'locked' flag of 'item' is not set. */
  iconRight?: React.ReactElement<any>;
}

export const DropdownButton = styled.button<{ $size?: Size }>`
  ${(props) => DropdownButtonCSS(props.$size)}
`;

export const ItemLabel = styled.div``;

export const ItemIcon = styled.div``;

export const ItemIconRight = styled.div`
  align-items: center;
  display: flex;
`;

export const ItemContent = styled.div<{$size:Size}>`
    margin: ${props=>props.$size===Size.Small?'auto 0 auto 10px':(props.$size===Size.Large?'auto 0 auto 26px':'auto 0 auto 18px')};
    display: flex;
    align-items: center;
    flex-grow: 2;

    ${ItemLabel} {
      margin: ${props => props.$size === Size.Small || props.$size === Size.Medium ? '14px 0' : '16px 0'};
      flex: 1 0 calc(100% - 40px);
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    ${ItemIcon} {
      margin-right: 8px;
      svg {
        display: block;
        margin: auto;
        height:${props=>props.$size===Size.Small?'20px':''};
        width:${props=>props.$size===Size.Small?'20px':''};
      }
    }

    ${ItemIconRight} {
      flex-grow: 2;

      .extraMargin {
        position: absolute;
        right: ${props=>props.$size===Size.Small?'16px':'24px'};
      }
    }
`;


export const DropdownButtonCSS = (size?: Size) => {
  return css`
    ${props => size == Size.Small && ComponentSStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
    ${props => size == Size.Medium && ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}
    ${props => size == Size.Large && ComponentLStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme))}

    position: relative;
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: ${size == Size.Large ? '64px' : size == Size.Medium ? '56px' : '48px'};
    background-color: ${props => COLORS.generateToken({ componentType:'bg-surface', defaultVariant:'default' }, props.theme)};
    border: none;
    cursor: pointer;
    background: none;
    width: 100%;
    text-align: left;

    span {
      margin: auto 0 auto 16px;
      line-height: 48px;
    }

    ${ItemLabel} {
      color: ${props => COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme)};
    }
    ${ItemIcon} {
      color: ${props => COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, props.theme)};
    }

    &:hover:not(.active):not(:disabled):not(.disabled-state),
    &.hover-state:not(.active):not(:disabled):not(.disabled-state) {
      background: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'hover' }, props.theme)};
      ${ItemLabel}, ${ItemIcon} {
        color: ${props => COLORS.generateToken({ componentType: 'text', state: 'hover' }, props.theme)};
      }
    }

    &:focus, &.focus-state, &.focus-visible {
      ${focusStyles}
    }

    &:active:not(.active):not(:disabled):not(.disabled-state), &.active-state {
      background: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'active' }, props.theme)};
      ${ItemLabel}, ${ItemIcon} {
        color: ${props => COLORS.generateToken({ componentType: 'text', state: 'active' }, props.theme)};
      }
    }

    &.active {
      background: ${props => COLORS.generateToken({ componentType: 'bg-surface', defaultVariant: 'selected' }, props.theme)};

      ${ItemLabel}, ${ItemIcon} {
        color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'default' }, props.theme)};
      }

      &::after {
        position: absolute;
        content: ' ';
        width: 4px;
        top: ${size == Size.Large ? '8px' : size == Size.Medium ? '6px' : '4px'};
        bottom: ${size == Size.Large ? '8px' : size == Size.Medium ? '6px' : '4px'};
        left: 4px;
        background-color: ${props => COLORS.generateToken({ componentType: 'border', defaultVariant: 'selected' }, props.theme)};

        border-radius: 4px;
      }
    }


    &.locked {
      background: none;
      cursor: not-allowed;
      
      ${ItemIcon}, ${ItemLabel} {
        color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme)};
      }
    }

    &.active:hover:not(:disabled):not(.locked), &.active-hover-state {
      background-color: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'hover' }, props.theme)};

      ${ItemIcon}, ${ItemLabel} {
        color: ${props => COLORS.generateToken({ componentType: 'text', state: 'hover' }, props.theme)};
      }      

      &::after {
        background-color: ${props => COLORS.generateToken({ componentType: 'border', state: 'hover' }, props.theme)};
      }
    }

    &.active:active:not(:disabled):not(.locked), &.active-active-state {
      background-color: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'active' }, props.theme)};

      ${ItemIcon}, ${ItemLabel} {
        color: ${props => COLORS.generateToken({ componentType: 'text', state: 'active' }, props.theme)};
      }

      &::after {
        background-color: ${props => COLORS.generateToken({ componentType: 'border', state: 'active' }, props.theme)};
      }
    }

    &.active.locked {
      &::after {
        background: none;
      } 
    }

    &.active:disabled:not(.locked) {
      &::after {
        background: none;
      }     
    }

    &:disabled:not(.locked), &.disabled-state {
      background: none;
      cursor: not-allowed;
      
      ${ItemIcon}, ${ItemLabel} {
        color: ${props => COLORS.generateToken({componentType:'text', state:'disabled'}, props.theme)} !important;
      }
    }

    &.dropdown-hover:not(:disabled) {
      background-color: ${props => COLORS.generateToken({ componentType:'bg-surface', state:'hover' }, props.theme)};
      color: ${props => COLORS.generateToken({ componentType:'text', state:'hover' }, props.theme)};
    }
  `;
};

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(({
                                                                       active,
                                                                       item,
                                                                       onClickHandler,
                                                                       size = Size.Medium,
                                                                       className,
                                                                       tabIndex,
                                                                       iconRight,
                                                                       id,
                                                                       ...rest
                                                                     }: MenuItemProps, ref) => {

    const theme = useTheme();

    return (
    <DropdownButton
        type="button"
        $size={size}
        disabled={item.disabled || item.locked}
        tabIndex={tabIndex}
        onMouseDown={defaultOnMouseDownHandler}
        ref={ref}
        onClick={onClickHandler}
        className={ className + ' ' + (item.locked ? 'locked' : '') + ' ' + (item.showDividerAbove ? 'show-divider-above' : '') + ' ' + (active ? 'active' : '')}
        id={id}
        {...rest}>
        {item.showDividerAbove && <div className="divider" />}
        <ItemContent $size={size}>
          {item.icon && <ItemIcon>{item.icon}</ItemIcon>}
          <ItemLabel>
              <div>
                <div>{item.displayLabel ?? item.value}</div>
                {item.noteLabel && <div style={{ fontSize: '12px', lineHeight: '16px' }}>{item.noteLabel}</div>}
              </div>
            {
              item.locked &&
              <ItemIconRight>
                <SystemIcons.LockedOn color={COLORS.generateToken({ componentType:'icon', defaultVariant:'subtle' }, theme)} className="extraMargin" size="24px"/>
              </ItemIconRight>
            }
            {
              !item.locked && !!iconRight &&
              <ItemIconRight>
                {React.cloneElement(iconRight, {className: 'extraMargin', size: '24px',})}
              </ItemIconRight>
            }

          </ItemLabel>
        </ItemContent>
  </DropdownButton>
  );
});

export default MenuItem;
